<template>
  <div>
    <Button 
      v-if="showHeader && !isEmbedRoute && !isPublicRoute" 
      variant="secondary"
      :icon="mobileSidebarOpen ? 'fas fa-times' : 'fas fa-bars'"
      @click="$emit('toggle-mobile-sidebar')"
      :aria-label="mobileSidebarOpen ? 'Fermer le menu' : 'Ouvrir le menu'"
      class="mobile-menu-button"
    />
    
    <div 
      v-if="showHeader && mobileSidebarOpen && !isEmbedRoute && !isPublicRoute" 
      class="sidebar-overlay"
      @click="$emit('close-mobile-sidebar')"
    ></div>
    
    <AppSidebarV2
      v-if="!isEmbedRoute && !isPublicRoute && showHeader"
      :show-header="showHeader"
      :mobile-sidebar-open="mobileSidebarOpen"
      :sidebar-collapsed="sidebarCollapsed"
      :is-admin="isAdmin"
      :is-super-admin="isSuperAdmin"
      :has-acces-pointeuse="hasAccesPointeuse"
      :user="user"
      @close-mobile="$emit('close-mobile-sidebar')"
      @toggle-sidebar="$emit('toggle-sidebar')"
      @logout="$emit('logout')"
    />
    <main class="main" :class="{ 'no-sidebar': !showHeader || isEmbedRoute || isPublicRoute, 'sidebar-collapsed': sidebarCollapsed && showHeader && !isEmbedRoute && !isPublicRoute }">
      <!-- CLÉ SUR LE CHEMIN, JAMAIS SUR `fullPath`. La clé sert à remonter la
           vue quand on passe d'une fiche à une autre (`/quotes/1` → `/quotes/2`),
           où la route est la même et le contenu entièrement différent. Le chemin
           dit exactement cela. `fullPath` y ajoutait la query : toute page qui
           écrit son état dans l'adresse — l'onglet actif d'une liste v2 (`?tab=`),
           un sous-onglet, une remise à zéro des filtres — se DÉTRUISAIT et se
           reconstruisait à chaque geste. D'où un double chargement par clic
           d'onglet, un clignotement du squelette entre les deux, et la perte de
           l'état que chaque onglet est censé garder. Ces pages écoutent déjà la
           query : elles n'ont pas besoin d'être remontées pour la voir changer. -->
      <router-view :key="$route.path" />
    </main>
    <Teleport to="body">
      <!-- Bandeaux du haut (mise à jour disponible) et du bas (édition, appel) :
           deux conteneurs fixes qui publient leur hauteur, pour que rien ne
           recouvre le début ni la fin du contenu. -->
      <!-- Maintenance : téléportés dans <body>, ces conteneurs échappent à
           l'attribut `inert` qu'App.vue pose sur ce composant. C'est
           `MaintenancePage` qui les rend inertes, avec tout le reste de
           <body> (popup d'appel, modales) : une seule règle, qui n'oublie pas
           le prochain `<Teleport>`. -->
      <TopBarsContainer
        v-if="!isEmbedRoute"
        :sidebar-collapsed="sidebarCollapsed && showHeader && !isEmbedRoute && !isPublicRoute"
      />
      <BottomBarsContainer
        v-if="!isEmbedRoute"
        :sidebar-collapsed="sidebarCollapsed && showHeader && !isEmbedRoute && !isPublicRoute"
      />
    </Teleport>
    <!-- Bandeau/popup d'appel : s'auto-téléporte (popup centré vers <body>, ou
         bandeau minimisé vers #bottom-bars-container). -->
    <CallBanner
      v-if="!isEmbedRoute"
      :call-in-progress="callInProgress"
      :call-duration="callDuration"
      :quote="currentCallQuote"
      :quote-id="currentCallQuoteId"
      :saving-call="savingCall"
      :call-direction="callDirection"
      :format-timer="formatTimer"
      @abandon-call="$emit('abandon-call')"
      @mark-no-answer="$emit('mark-no-answer')"
      @mark-not-interested="$emit('mark-not-interested')"
      @schedule-relance="$emit('schedule-relance')"
      @validate-info="$emit('validate-info')"
    />
    <!-- Décalé au-dessus des bandeaux fixes (édition, appel) via --bottom-bars-height,
         pour que les notifications ne recouvrent jamais la barre de modification. -->
    <Toaster
      v-if="!isEmbedRoute"
      position="bottom-right"
      close-button
      :toast-options="{ closeButtonAriaLabel: 'Fermer la notification' }"
      :offset="{ bottom: 'calc(var(--bottom-bars-height, 0px) + 24px)' }"
      :mobile-offset="{ bottom: 'calc(var(--bottom-bars-height, 0px) + 16px)' }"
      data-testid="success-toast"
    />
  </div>
</template>

<script setup>
import { Teleport } from 'vue'
import { Toaster } from 'vue-sonner'
import AppSidebarV2 from './AppSidebarV2.vue'
import CallBanner from '../calls/CallBanner.vue'
import BottomBarsContainer from '../shared/BottomBarsContainer.vue'
import TopBarsContainer from '../shared/TopBarsContainer.vue'
import Button from '../ui/Button.vue'

defineProps({
  showHeader: Boolean,
  isEmbedRoute: Boolean,
  isPublicRoute: Boolean,
  mobileSidebarOpen: Boolean,
  sidebarCollapsed: Boolean,
  isAdmin: Boolean,
  // Accès au panneau d'activité commerciale — relayé jusqu'à
  // AppSidebarNavigation (consommé par un autre lot).
  isSuperAdmin: Boolean,
  // Rôle complémentaire « pointeuse » — relayé jusqu'à AppSidebarV2Navigation.
  hasAccesPointeuse: Boolean,
  user: Object,
  callInProgress: Boolean,
  callDuration: Number,
  currentCallQuote: Object,
  currentCallQuoteId: Number,
  savingCall: Boolean,
  callDirection: String,
  formatTimer: Function
})

defineEmits([
  'toggle-mobile-sidebar',
  'close-mobile-sidebar',
  'toggle-sidebar',
  'logout',
  'abandon-call',
  'mark-no-answer',
  'mark-not-interested',
  'schedule-relance',
  'validate-info'
])
</script>

<style scoped>
.main {
  margin: 0;
  min-height: 100vh;
  box-sizing: border-box;
}

.main.no-sidebar {
  margin-left: 0;
  width: 100%;
  padding: 0;
}

.main:not(.no-sidebar) {
  margin-left: var(--sidebar-width);
  width: calc(100% - var(--sidebar-width));
  padding: var(--spacing-10) var(--spacing-8);
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Rail de la barre repliée : 96 px. */
.main.sidebar-collapsed {
  margin-left: 96px;
  width: calc(100% - 96px);
}

.mobile-menu-button {
  display: none;
  position: fixed;
  /* Passe sous le bandeau du haut quand il y en a un : la variable vaut 0 le
     reste du temps, le bouton ne bouge donc pas. */
  top: calc(var(--top-bars-height, 0px) + var(--spacing-4));
  left: var(--spacing-4);
  z-index: calc(var(--z-fixed) + 1);
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: var(--shadow-sm);
  transition: all var(--transition-base);
  touch-action: manipulation;
}

.mobile-menu-button:hover {
  background: var(--color-bg-secondary);
}

.mobile-menu-button i {
  font-size: 1.2rem;
  color: var(--color-text-primary);
}

.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--color-bg-overlay);
  z-index: calc(var(--z-fixed) - 1);
  display: none;
}

@media (max-width: 768px) {
  .mobile-menu-button {
    display: flex;
  }
  
  .sidebar-overlay {
    display: block;
  }
  
  .main:not(.no-sidebar) {
    margin-left: 0;
    width: 100%;
    padding: var(--spacing-4);
    padding-top: calc(var(--spacing-4) + 44px + var(--spacing-4));
  }
  
  .main {
    padding: var(--spacing-4);
    padding-top: calc(var(--spacing-4) + 44px + var(--spacing-4));
  }
}
</style>
